// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline component for visualizing asset event groups.
 * This module provides a comprehensive timeline view that shows
 * both project and logistics events for an asset. It includes
 * zoom controls, legend tooltips, and interactive features.
 *
 * Key features:
 * - Project timeline
 * - Logistics timeline
 * - Zoom controls
 * - Interactive legend
 * - Responsive layout
 */
import { Asset } from '@/asset/api/types';
import { AssetLogisticsTimeline } from '@/asset/components/asset-logistics-timeline';
import { AssetProjectTimeline } from '@/asset/components/asset-project-timeline';
import { TimelineControls } from '@/components/timeline-controls';
import { TimelineLegendTooltip } from '@/components/timeline-legend-tooltip';
import { LegendToggleRounded } from '@/constants/hardfin-standard-icons';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { useTimelineState } from '@/hooks/use-timeline-state';
import { Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useRef } from 'react';

/**
 * Height of each timeline bar in pixels.
 */
const BAR_HEIGHT = 30;

/**
 * Vertical margin between timeline bars in pixels.
 */
const BAR_MARGIN = 8;

/**
 * Props for configuring the AssetEventGroupTimeline component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset for which to display event groups.
   * Used to fetch and display timeline data.
   */
  asset: Asset;

  /**
   * Whether timeline interactions are disabled.
   * When true, prevents editing and interaction.
   *
   * @default false
   */
  isDisabled?: boolean;
}

/**
 * A timeline component for visualizing asset event groups.
 * Displays a comprehensive timeline view showing both project
 * and logistics events for an asset. Includes zoom controls,
 * legend tooltips, and interactive features.
 *
 * Features:
 * - Project timeline
 * - Logistics timeline
 * - Zoom controls
 * - Interactive legend
 * - Responsive layout
 * - Timeline navigation
 * - Visual grouping
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ asset }: Props) {
 *   return (
 *     <AssetEventGroupTimeline
 *       asset={asset}
 *       isDisabled={false}
 *     />
 *   );
 * }
 * ```
 */
export function AssetEventGroupTimeline(props: Props) {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const tooltip = useDisclosure();
  const resizeObserver = useResizeObserver(ref);
  const timelineState = useTimelineState(resizeObserver.width, 'week');
  const tooltipAnchorRef = useRef<SVGSVGElement>(null);

  return (
    <Paper
      ref={ref}
      variant="outlined"
      sx={{ position: 'relative', boxShadow: theme.shadows[6] }}
    >
      <Stack
        useFlexGap
        direction="row"
        alignItems="center"
        spacing={1}
        paddingX={2}
        paddingY={1}
      >
        <Typography variant="subtitle1">Timeline</Typography>
        <LegendToggleRounded
          ref={tooltipAnchorRef}
          fontSize="small"
          color="action"
          onClick={tooltip.open}
          sx={{ marginRight: 'auto', cursor: 'help' }}
        />
        <Typography variant="body2" marginRight={1}>
          {timelineState.interval.toLocaleString({ dateStyle: 'medium' })}
        </Typography>
        <TimelineProvider>
          <TimelineLegendTooltip
            isOpen={tooltip.isOpen}
            onClose={tooltip.close}
            anchorElement={tooltipAnchorRef.current}
          />
        </TimelineProvider>
        <Divider flexItem orientation="vertical" />
        <TimelineControls
          density={timelineState.zoom}
          canZoomIn={timelineState.canZoomIn}
          canZoomOut={timelineState.canZoomOut}
          onClickZoomIn={timelineState.zoomIn}
          onClickZoomOut={timelineState.zoomOut}
          onClickForward={timelineState.shiftForward}
          onClickBackward={timelineState.shiftBackward}
        />
      </Stack>
      <Divider />
      <AssetProjectTimeline
        asset={props.asset}
        zoom={timelineState.zoom}
        interval={timelineState.interval}
        barHeight={BAR_HEIGHT}
        barMargin={BAR_MARGIN}
        isDisabled={props.isDisabled}
      />
      <Divider />
      <AssetLogisticsTimeline
        asset={props.asset}
        zoom={timelineState.zoom}
        interval={timelineState.interval}
        barHeight={BAR_HEIGHT}
        barMargin={BAR_MARGIN}
      />
    </Paper>
  );
}
